import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users as UsersIcon, Mail, Phone, Shield, Building, RefreshCw, User, Crown, Truck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useUsersFromSupabase } from "@/hooks/useSupabaseUsers";
import { useUpdateUser } from "@/hooks/useSupabaseUsers";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import PageLoader from "@/components/PageLoader";
import PageTransition from "@/components/PageTransition";
import { useCompanyNameByLegacyId } from "@/hooks/useSupabaseCompanies";
import { useAuth } from "@/components/AuthProvider";
import { isAdminEmail } from "@/lib/adminConfig";

// Company Display Component
const CompanyDisplay = ({ compId }: { compId?: string }) => {
  const companyName = useCompanyNameByLegacyId(compId);
  
  if (!compId) {
    return <span className="text-muted-foreground">-</span>;
  }
  
  return (
    <Badge variant="outline" className="flex items-center gap-1 w-fit">
      <Building className="h-3 w-3" />
      {companyName}
    </Badge>
  );
};

const Users = () => {
  const { users, loading, error, refresh } = useUsersFromSupabase();
  const { user: authUser, isBOD } = useAuth();
  const isAdmin = isAdminEmail(authUser?.email);
  const canManageSales = isAdmin || isBOD;
  const updateUser = useUpdateUser();
  const { toast } = useToast();

  const handleToggleBOD = async (supabaseId: string, userName: string, currentIsBOD: boolean) => {
    try {
      await updateUser.mutateAsync({
        id: supabaseId,
        updates: { is_bod: !currentIsBOD },
      });
      toast({
        title: currentIsBOD ? "Đã gỡ quyền BOD" : "Đã cấp quyền BOD",
        description: `${userName} ${currentIsBOD ? "không còn là" : "đã trở thành"} BOD.`,
      });
    } catch {
      toast({
        title: "Lỗi",
        description: "Không thể cập nhật quyền BOD. Vui lòng thử lại.",
        variant: "destructive",
      });
    }
  };

  const handleToggleSales = async (supabaseId: string, userName: string, currentIsSales: boolean) => {
    try {
      await updateUser.mutateAsync({
        id: supabaseId,
        updates: { is_sales: !currentIsSales } as { is_sales: boolean },
      });
      toast({
        title: currentIsSales ? "Đã gỡ quyền Sales" : "Đã cấp quyền Sales",
        description: `${userName} ${currentIsSales ? "không còn là" : "đã trở thành"} Sales.`,
      });
    } catch {
      toast({
        title: "Lỗi",
        description: "Không thể cập nhật quyền Sales. Vui lòng thử lại.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <PageLoader type="table" title="Đang tải dữ liệu users..." />;
  }

  if (error) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center bg-background">
          <Card className="w-full max-w-md">
            <CardContent className="flex flex-col items-center justify-center py-8">
              <p className="text-destructive mb-4">Lỗi: {error}</p>
              <Button onClick={refresh} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Thử lại
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageTransition>
    );
  }

  const adminUsers = users.filter(user => user.IsAdmin);
  const bodUsers = users.filter(user => user.IsBOD);
  const salesUsers = users.filter(user => user.IsSales);
  const regularUsers = users.filter(user => !user.IsAdmin && !user.IsBOD && !user.IsSales);

  return (
    <PageTransition>
      <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Quản lý Users</h1>
          <p className="text-xl text-muted-foreground">Quản lý và theo dõi người dùng trong hệ thống</p>
          
          <div className="flex gap-4 mt-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-red-500" />
              <span className="text-sm">Admin: {adminUsers.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <Crown className="h-4 w-4 text-amber-500" />
              <span className="text-sm">BOD: {bodUsers.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-emerald-600" />
              <span className="text-sm">Sales: {salesUsers.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-blue-500" />
              <span className="text-sm">Regular: {regularUsers.length}</span>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <UsersIcon className="h-5 w-5" />
                Danh sách Users ({users.length})
              </CardTitle>
              <div className="flex gap-2">
                <Button onClick={refresh} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Làm mới
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Teams</TableHead>
                    <TableHead>Role</TableHead>
                    {isBOD && <TableHead>BOD</TableHead>}
                    {canManageSales && <TableHead>Sales</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.ID}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.Avatar ?? undefined} alt={user.UserName ?? ''} />
                            <AvatarFallback>
                              {user.UserName ? user.UserName.substring(0, 2).toUpperCase() : 
                               user.Email.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {user.UserName || 'N/A'}
                            </span>
                            <span className="text-sm text-muted-foreground">ID: {user.ID}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{user.Email}</span>
                          </div>
                          {user.PhoneNumber && (
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">{user.PhoneNumber}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <CompanyDisplay compId={user.CompID ?? undefined} />
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {user.UGroup ? (
                          <div className="flex flex-wrap gap-1">
                            {user.UGroup.split(',').map((teamId, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {teamId.trim()}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {user.IsAdmin && (
                            <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                              <Shield className="h-3 w-3" />
                              Admin
                            </Badge>
                          )}
                          {user.IsBOD && (
                            <Badge className="flex items-center gap-1 w-fit bg-amber-500 hover:bg-amber-600">
                              <Crown className="h-3 w-3" />
                              BOD
                            </Badge>
                          )}
                          {user.IsSales && (
                            <Badge className="flex items-center gap-1 w-fit bg-emerald-600 hover:bg-emerald-700">
                              <Truck className="h-3 w-3" />
                              Sales
                            </Badge>
                          )}
                          {!user.IsAdmin && !user.IsBOD && !user.IsSales && (
                            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                              <User className="h-3 w-3" />
                              User
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      {isBOD && (
                        <TableCell>
                          <Button
                            size="sm"
                            variant={user.IsBOD ? "destructive" : "outline"}
                            disabled={updateUser.isPending}
                            onClick={() =>
                              handleToggleBOD(
                                user.supabaseId,
                                user.UserName || user.Email,
                                user.IsBOD ?? false
                              )
                            }
                          >
                            <Crown className="h-3 w-3 mr-1" />
                            {user.IsBOD ? "Gỡ BOD" : "Cấp BOD"}
                          </Button>
                        </TableCell>
                      )}
                      {canManageSales && (
                        <TableCell>
                          <Button
                            size="sm"
                            variant={user.IsSales ? "destructive" : "outline"}
                            disabled={updateUser.isPending}
                            onClick={() =>
                              handleToggleSales(
                                user.supabaseId,
                                user.UserName || user.Email,
                                user.IsSales ?? false
                              )
                            }
                          >
                            <Truck className="h-3 w-3 mr-1" />
                            {user.IsSales ? "Gỡ Sales" : "Cấp Sales"}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {users.length === 0 && (
              <div className="text-center py-8">
                <UsersIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Không có user nào</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </PageTransition>
  );
};

export default Users;
